import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LockKeyhole, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) navigate("/esqueci-senha", { replace: true });
  }, [token, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/entrar", { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível redefinir a senha.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="container-cuidar py-16 max-w-lg">
        <div className="surface p-8 text-center" data-testid="reset-success-panel">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[hsl(var(--primary))]" />
          <h1 className="mt-4 font-heading text-3xl font-black">Senha redefinida!</h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">Você já pode entrar com a senha nova. Estamos te redirecionando…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-cuidar py-16 max-w-lg">
      <div className="surface p-8">
        <LockKeyhole className="h-8 w-8 text-[hsl(var(--primary))]" />
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-black leading-tight">Criar nova senha</h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))]">Escolha uma senha com pelo menos 6 caracteres. Depois é só logar de novo 🌱</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="password">Nova senha</Label>
            <Input id="password" type="password" required minLength={6} autoComplete="new-password" data-testid="reset-password-input"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirme a senha</Label>
            <Input id="confirm" type="password" required minLength={6} autoComplete="new-password" data-testid="reset-confirm-input"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 h-12 rounded-xl" />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="btn-pill w-full h-12 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
            {loading ? "Salvando…" : "Redefinir senha"}
          </Button>
        </form>
        <div className="mt-4 text-sm text-center">
          <Link to="/entrar" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:underline">Voltar ao login</Link>
        </div>
      </div>
    </div>
  );
}
